import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Grid2 as Grid, IconButton, Typography } from '@mui/material';
import React, { useState } from 'react';
import { MdAirlineSeatReclineExtra, MdSpeed } from 'react-icons/md';
import { TbEngine } from 'react-icons/tb';
import { useSwipeable } from 'react-swipeable';
import { FleetImage, FleetItem } from '../../pages/FleetPage';

interface FleetItemDisplayProps {
    readonly asset: FleetItem;
    readonly reverse?: boolean;
}

const FONT = '"DM Sans", system-ui, -apple-system, sans-serif';

const fleetItemSx = {
    card: {
        backgroundColor: 'background.paper',
        borderRadius: { xs: '18px', sm: '24px' },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 18px 48px rgba(15, 23, 42, 0.09)',
        overflow: 'hidden',
    },
    grid: {
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'stretch',
    },
    gridReverse: {
        flexDirection: { xs: 'column', md: 'row-reverse' },
        alignItems: 'stretch',
    },
    mediaCol: {
        position: 'relative',
        display: 'flex',
    },
    imageBox: {
        position: 'relative',
        width: '100%',
        height: { xs: 'auto', md: '100%' },
        minHeight: { md: 300 },
        aspectRatio: { xs: '16 / 9', md: 'auto' },
        overflow: 'hidden',
        backgroundColor: 'rgba(15, 23, 42, 0.04)',
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
    },
    arrow: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        color: 'text.primary',
        backdropFilter: 'blur(6px)',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.18)',
        padding: '6px',
        transition: 'background-color 0.2s ease',
        '&:hover': { backgroundColor: '#fff' },
        '& .MuiSvgIcon-root': { fontSize: '1.4rem' },
    },
    arrowLeft: { left: 12 },
    arrowRight: { right: 12 },
    dots: {
        position: 'absolute',
        bottom: 12,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        zIndex: 2,
    },
    content: {
        padding: { xs: '22px 20px 26px', sm: '32px', md: '40px' },
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        color: 'text.primary',
        fontWeight: 700,
        fontSize: { xs: '1.7rem', sm: '2rem' },
        lineHeight: 1.1,
        letterSpacing: '-0.5px',
        fontFamily: FONT,
    },
    factsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginTop: '18px',
        marginBottom: '6px',
    },
    factChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        padding: '7px 12px',
        borderRadius: '999px',
        backgroundColor: 'background.subtle',
        border: '1px solid rgba(247, 97, 0, 0.16)',
        color: 'text.primary',
        fontSize: '0.82rem',
        fontWeight: 600,
        fontFamily: FONT,
        lineHeight: 1.2,
    },
    factIcon: {
        color: 'primary.main',
        fontSize: '1.05rem',
        display: 'inline-flex',
    },
    description: {
        marginTop: '18px',
        color: 'text.soft',
        fontSize: { xs: '0.95rem', sm: '1rem' },
        lineHeight: 1.7,
        fontFamily: FONT,
    },
} as const;

function FleetImageView({ image }: { readonly image: FleetImage }) {
    return (
        <picture style={{ display: 'block', width: '100%', height: '100%' }}>
            <source srcSet={image.src} type="image/webp" />
            <Box
                component="img"
                src={image.fallback}
                alt={image.alt}
                loading="lazy"
                draggable={false}
                sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                }}
            />
        </picture>
    );
}

function Carousel({ images }: { readonly images: FleetImage[] }) {
    const [current, setCurrent] = useState(0);
    const length = images.length;
    const hasMultiple = length > 1;

    const goTo = (next: number) => setCurrent(((next % length) + length) % length);
    const handleNext = () => goTo(current + 1);
    const handlePrev = () => goTo(current - 1);

    const handlers = useSwipeable({
        onSwipedLeft: handleNext,
        onSwipedRight: handlePrev,
        trackMouse: true,
    });

    return (
        <Box sx={fleetItemSx.imageBox} {...handlers}>
            {images.map((image, i) => (
                <Box
                    key={image.src}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        opacity: i === current ? 1 : 0,
                        transition: 'opacity 0.6s ease',
                        pointerEvents: i === current ? 'auto' : 'none',
                    }}
                >
                    <FleetImageView image={image} />
                </Box>
            ))}

            {hasMultiple && (
                <>
                    <IconButton
                        aria-label="Previous image"
                        onClick={handlePrev}
                        sx={[fleetItemSx.arrow, fleetItemSx.arrowLeft]}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                        aria-label="Next image"
                        onClick={handleNext}
                        sx={[fleetItemSx.arrow, fleetItemSx.arrowRight]}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                    <Box sx={fleetItemSx.dots}>
                        {images.map((image, i) => (
                            <Box
                                key={`dot-${image.src}`}
                                component="button"
                                aria-label={`Go to image ${i + 1}`}
                                onClick={() => goTo(i)}
                                sx={{
                                    width: i === current ? 22 : 8,
                                    height: 8,
                                    padding: 0,
                                    border: 'none',
                                    borderRadius: '999px',
                                    cursor: 'pointer',
                                    backgroundColor: i === current ? 'primary.main' : 'rgba(255, 255, 255, 0.7)',
                                    boxShadow: '0 1px 4px rgba(15, 23, 42, 0.25)',
                                    transition: 'all 0.3s ease',
                                }}
                            />
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
}

function FactChip({ icon, children }: { readonly icon: React.ReactNode; readonly children: React.ReactNode }) {
    return (
        <Box sx={fleetItemSx.factChip}>
            <Box component="span" sx={fleetItemSx.factIcon}>
                {icon}
            </Box>
            {children}
        </Box>
    );
}

export default function FleetItemDisplay({ asset, reverse }: FleetItemDisplayProps) {
    return (
        <Box sx={fleetItemSx.card}>
            <Grid container sx={reverse ? fleetItemSx.gridReverse : fleetItemSx.grid}>
                <Grid size={{ xs: 12, md: 6 }} sx={fleetItemSx.mediaCol}>
                    <Carousel images={asset.images} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={fleetItemSx.content}>
                        <Typography component="h2" sx={fleetItemSx.title}>
                            {asset.name}
                        </Typography>

                        <Box sx={fleetItemSx.factsRow}>
                            <FactChip icon={<MdSpeed />}>{asset.keyFacts.speed}</FactChip>
                            <FactChip icon={<MdAirlineSeatReclineExtra />}>{asset.keyFacts.seats}</FactChip>
                            <FactChip icon={<TbEngine />}>{asset.keyFacts.power}</FactChip>
                        </Box>

                        <Typography sx={fleetItemSx.description}>{asset.description}</Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
